import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import CICDBannerImage from "../../../../assets/technologies/tools-and-languages/uvm/uvmbannerimage.png";
import ProfileImage from "../../../../assets/blogpages/person.png";
import "./CICD.css";
import { AnimatePresence, motion } from "framer-motion";

// Wraps matching substrings in <mark> for search highlighting
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i}>{part}</mark>
      : part
  );
};

const CICD = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [replyVisible, setReplyVisible] = useState({});

  const closeOverlay = () => setShowPopup(false);
  const handleSearch = () => setSearchTerm(searchInput.trim());
  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };
  const handleReplySubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };
  const toggleReply = id =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  // Section refs for scroll-to
  const sectionRefs = {
    intro: useRef(null),
    benefits: useRef(null),
    pipeline: useRef(null),
    tools: useRef(null),
    automation: useRef(null),
    integration: useRef(null),
    monitoring: useRef(null),
    security: useRef(null),
    bestPractices: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };
  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  // Scroll to first highlighted match
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // Fade-in sections on scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add("visible");
        else e.target.classList.remove("visible");
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Auto-dismiss popup
  useEffect(() => {
    if (showPopup) {
      const t = setTimeout(() => setShowPopup(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showPopup]);

  return (
    <div className="blog-post-page cicd">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>CI/CD for ASIC Development</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-verification">Services</Link> | CI/CD
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search keyword..."
            className="search-input"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          <button onClick={handleSearch} className="search-button">Search</button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    benefits: "CI/CD Benefits",
                    pipeline: "Building Your Pipeline",
                    tools: "EDA & Build Tools",
                    automation: "Automation Frameworks",
                    integration: "VCS Integration",
                    monitoring: "Dashboard & Alerts",
                    security: "Security & Compliance",
                    bestPractices: "Best Practices",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content */}
      <div className="blog-content-container">
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={CICDBannerImage}
            alt="CI/CD Pipeline"
          />
          <div className="post-info-row fade-section">
            <span className="post-author">By Alex DevOps</span>
            <span className="post-category">Category: ASIC Development</span>
            <button
              className="comment-link"
              onClick={() => scrollTo(sectionRefs.comments)}
            >
              3 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">Introduction</h2>
          <p className="fade-section">
            <Highlight
              text="Continuous Integration and Continuous Delivery (CI/CD) bring software engineering best practices to ASIC development, automating builds, regressions, and artifact management to accelerate time-to-silicon."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="By integrating EDA tool runs—synthesis, place-and-route, timing analysis, and regression tests—into automated pipelines, teams catch integration bugs early and ensure consistent, repeatable results."
              query={searchTerm}
            />
          </p>

          {/* 2. CI/CD Benefits */}
          <h2 ref={sectionRefs.benefits} className="fade-section">CI/CD Benefits</h2>
          <p className="fade-section">
            <Highlight
              text="Automated pipelines reduce manual errors, enforce standards, and provide rapid feedback on code changes, minimizing the risk of costly silicon respins."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Artifact versioning ensures that every netlist, test suite, and coverage report is traceable to a specific commit, improving collaboration and compliance."
              query={searchTerm}
            />
          </p>

          {/* 3. Building Your Pipeline */}
          <h2 ref={sectionRefs.pipeline} className="fade-section">Building Your Pipeline</h2>
          <p className="fade-section">
            <Highlight
              text="Start with a code checkout stage, followed by lint and static checks on SystemVerilog/RTL, then run unit regressions and coverage analysis in parallel to minimize turnaround."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Integrate synthesis and place-and-route stages with artifact caching so that incremental builds only re-run what’s necessary."
              query={searchTerm}
            />
          </p>

          {/* 4. EDA & Build Tools */}
          <h2 ref={sectionRefs.tools} className="fade-section">EDA & Build Tools</h2>
          <p className="fade-section">
            <Highlight
              text="Common CI servers (Jenkins, GitLab CI) orchestrate Dockerized EDA containers—Synopsys, Cadence, Mentor—ensuring environment consistency across engineers and agents."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use Makefiles or Python scripts to coordinate multi-step flows, parse logs, and fail fast on errors."
              query={searchTerm}
            />
          </p>

          {/* 5. Automation Frameworks */}
          <h2 ref={sectionRefs.automation} className="fade-section">Automation Frameworks</h2>
          <p className="fade-section">
            <Highlight
              text="Frameworks like Robut or custom Python libraries manage job submissions to cluster schedulers, collect results, and aggregate coverage metrics automatically."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Automated post-processing scripts generate unified HTML dashboards and notifications for pass/fail, coverage drop, or timing violations."
              query={searchTerm}
            />
          </p>

          {/* 6. Version Control Integration */}
          <h2 ref={sectionRefs.integration} className="fade-section">Version Control Integration</h2>
          <p className="fade-section">
            <Highlight
              text="Trigger pipelines on pull requests or commits, gating merges until all stages pass. Use commit statuses in GitHub/GitLab to enforce pipeline health."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Tag releases and attach build artifacts—netlists, coverage reports, binaries—to ensure reproducibility."
              query={searchTerm}
            />
          </p>

          {/* 7. Dashboard & Alerts */}
          <h2 ref={sectionRefs.monitoring} className="fade-section">Dashboard & Alerts</h2>
          <p className="fade-section">
            <Highlight
              text="Aggregate results into a central dashboard showing build status, regression history, coverage trends, and resource usage."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Configure email or Slack notifications on failures or coverage regressions to keep the team informed in real time."
              query={searchTerm}
            />
          </p>

          {/* 8. Security & Compliance */}
          <h2 ref={sectionRefs.security} className="fade-section">Security & Compliance</h2>
          <p className="fade-section">
            <Highlight
              text="Scan RTL for security vulnerabilities (e.g., hard-coded keys), enforce license compliance for third-party IP, and audit all pipeline executions for traceability."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use immutable build agents and secure credential management to prevent unauthorized access to IP."
              query={searchTerm}
            />
          </p>

          {/* 9. Best Practices */}
          <h2 ref={sectionRefs.bestPractices} className="fade-section">Best Practices</h2>
          <p className="fade-section">
            <Highlight
              text="Keep pipeline steps modular, with clear inputs and outputs. Version your automation scripts alongside RTL so changes remain in sync."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Regularly prune and archive old artifacts to conserve storage, and periodically review pipeline performance for optimization."
              query={searchTerm}
            />
          </p>

          {/* 10. Future Trends */}
          <h2 ref={sectionRefs.trends} className="fade-section">Future Trends</h2>
          <p className="fade-section">
            <Highlight
              text="AI-driven pipeline optimization will predict regression flakiness and auto-adjust resource allocation for shorter feedback loops."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Standardized CI/CD tooling for semiconductor (e.g., cloud-based EDA as a service) will further lower barriers to entry."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>3 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">CI Enthusiast</span>
                    <span className="comment-date">May 15, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Great breakdown of pipeline stages—this helped our ASIC team standardize builds!
              </p>
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Flow Master</span>
                    <span className="comment-date">May 16, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Automating place-and-route caching cut our build times in half. Thanks for the tips!
              </p>
              <button className="reply-button" onClick={() => toggleReply("flowmaster")}>Reply</button>
              {replyVisible["flowmaster"] && (
                <div className="leave-comment">
                  <h3>Reply to Flow Master</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">Reply</button>
                  </form>
                </div>
              )}
            </div>

            {/* Comment 3 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Secure Dev</span>
                    <span className="comment-date">May 17, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Appreciate the security section—imperative for our gated flows!
              </p>
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here…" required />
                </div>
                <button type="submit" className="send-message-btn">Send Message</button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search keyword..."
              className="search-input"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            <button onClick={handleSearch} className="search-button">Search</button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      benefits: "CI/CD Benefits",
                      pipeline: "Building Your Pipeline",
                      tools: "EDA & Build Tools",
                      automation: "Automation Frameworks",
                      integration: "VCS Integration",
                      monitoring: "Dashboard & Alerts",
                      security: "Security & Compliance",
                      bestPractices: "Best Practices",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showPopup && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CICD;